import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from 'react';
import { useAccountBootstrap } from '../hooks/useAccountBootstrap';
import { useDogsFromCustomer } from '../hooks/useDogsFromCustomer';
import { useNutritionEngine } from '../hooks/useNutritionEngine';
import { useDogsManagement } from '../hooks/useDogsManagement';
import { fetchPaymentUpdateUrl, updateSubscriptionMeals, skipNextDelivery, sendPaymentUpdateEmail, fetchAvailableDeliveryDates } from '../api/accountProxy';
import { normalizeSubscriptions, isActiveSub, getNextDeliveryDate } from '../utils/recharge';
import type {
  Dog,
  DogFormData,
  UpdateDogResult,
  NutritionRecalculationResult,
  ShopifyCustomer,
  ShopifyProduct,
  RechargeSubscription,
  RechargeCharge,
  RechargeOrder,
  PaymentMethod,
  Recipe,
  NavKey,
} from '../types';

export type BasketItem = {
  subscriptionId: string;
  variantId: string;
  productId: string;
  productTitle: string;
  variantTitle: string;
  imageUrl: string | null;
  quantity: number;
  price: number;
};

// Error tracking for production-grade error handling
export type AccountError = {
  id: string;
  message: string;
  code?: string;
  context?: string; // e.g., "fetchPendingOnetimes", "checkoutBasket"
  timestamp: number;
  dismissed?: boolean;
};

interface AccountContextValue {
  // Data
  customer: ShopifyCustomer | null;
  dogs: Dog[];
  subscriptions: RechargeSubscription[];
  upcoming: RechargeCharge[];
  orders: RechargeOrder[];
  addresses: any[];
  payments: PaymentMethod[];
  products: ShopifyProduct[];
  recipes: Recipe[];

  // Computed
  hasSubs: boolean;
  activeSubsCount: number;
  nextDeliveryDate: string | null;

  // Loading state
  loading: boolean;
  error?: string;

  // Error tracking (production-grade)
  errors: AccountError[];
  addError: (error: Omit<AccountError, 'id' | 'timestamp'>) => void;
  dismissError: (errorId: string) => void;
  clearErrors: () => void;

  // Global loading overlay
  globalLoading: boolean;
  globalLoadingMessage: string | null;
  showLoading: (message?: string) => void;
  hideLoading: () => void;

  // Computed
  activeDogs: Dog[];

  // Actions
  refresh: () => Promise<void>;
  getDogById: (dogId: string) => Dog | undefined;
  updateDog: (dogId: string, updates: Partial<DogFormData>) => Promise<UpdateDogResult>;
  softDeleteDog: (dogId: string) => Promise<void>;
  restoreDog: (dogId: string) => Promise<void>;
  saveDogWithNutrition: (
    dogId: string,
    updates: Partial<DogFormData>
  ) => Promise<NutritionRecalculationResult | null>;
  recalculateDogNutrition: (dog: Dog) => NutritionRecalculationResult;
  startPaymentUpdate: () => Promise<string | null>;
  sendPaymentUpdateEmail: (paymentMethodId?: string) => Promise<{ ok: boolean; error?: string }>;
  skipDelivery: (subscriptionId: string, addressId?: string) => Promise<{ newDeliveryDate?: string }>;

  // UI state
  nav: NavKey;
  setNav: (nav: NavKey) => void;
  isDevAccessible: boolean;
  isAdminEmail: boolean;

  // Basket state
  basket: BasketItem[];
  basketItemCount: number;
  addToBasket: (subscriptionId: string, items: Omit<BasketItem, 'subscriptionId'>[]) => void;
  clearBasket: () => void;
  removeFromBasket: (variantId: string) => void;
  checkoutBasket: (newPlanDays?: number | null, oneTimeAddition?: boolean) => Promise<void>;

  // Dog navigation
  selectedDogId: string | null;
  setSelectedDogId: (id: string | null) => void;

  // Internal
  effectiveShop: string;
  effectiveProxyBase: string;
}

const AccountContext = createContext<AccountContextValue | null>(null);

interface AccountProviderProps {
  children: React.ReactNode;
  customerJSON: any;
  shopDomain: string | null;
  proxyBase: string;
  products: ShopifyProduct[];
}

function unwrapShopifyProducts(maybe: any): ShopifyProduct[] {
  const arr = Array.isArray(maybe)
    ? maybe
    : Array.isArray(maybe?.products)
    ? maybe.products
    : Array.isArray(maybe?.data?.products)
    ? maybe.data.products
    : [];

  return arr.map((p: any) => (p?.product ? p.product : p));
}

export function AccountProvider({
  children,
  customerJSON,
  shopDomain,
  proxyBase,
  products: productsFromShopify,
}: AccountProviderProps) {
  // Valid nav keys for URL hash validation
  const validNavKeys: NavKey[] = ["dashboard", "upcoming", "orders", "subscriptions", "extras", "address", "payments", "dogs", "dog-profile", "dog-edit", "help", "dev"];

  // Customers land on Subscriptions — that's where all the manage actions
  // live. Deep links (#upcoming, #orders, …) always win over this default.
  const defaultNav: NavKey = "subscriptions";

  // Get initial nav from URL hash
  const getNavFromHash = (): NavKey => {
    if (typeof window === "undefined") return defaultNav;
    const hash = window.location.hash.replace("#", "");
    return validNavKeys.includes(hash as NavKey) ? (hash as NavKey) : defaultNav;
  };

  // Navigation state - initialize from URL hash
  const [nav, setNavState] = useState<NavKey>(getNavFromHash);

  // Wrapper to update both state and URL hash. pushState (not replaceState):
  // each in-portal navigation must create a history entry, so the browser
  // back button walks back through portal pages instead of dropping straight
  // out to the login page (#55). The popstate listener below restores state.
  const setNav = useCallback((newNav: NavKey) => {
    setNavState(newNav);
    if (typeof window !== "undefined" && window.location.hash !== `#${newNav}`) {
      window.history.pushState(null, "", `#${newNav}`);
    }
  }, []);

  // Listen for browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      setNavState(getNavFromHash());
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Selected dog for profile/edit navigation
  const [selectedDogId, setSelectedDogId] = useState<string | null>(null);

  // Global loading overlay state
  const [globalLoading, setGlobalLoading] = useState(false);
  const [globalLoadingMessage, setGlobalLoadingMessage] = useState<string | null>(null);

  const showLoading = useCallback((message?: string) => {
    setGlobalLoadingMessage(message || null);
    setGlobalLoading(true);
  }, []);

  const hideLoading = useCallback(() => {
    setGlobalLoading(false);
    setGlobalLoadingMessage(null);
  }, []);

  // Error tracking state (production-grade)
  const [errors, setErrors] = useState<AccountError[]>([]);

  const addError = useCallback((error: Omit<AccountError, 'id' | 'timestamp'>) => {
    const newError: AccountError = {
      ...error,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
    };
    console.error(`[AccountError] ${error.context || 'Unknown'}: ${error.message}`, error.code ? `(${error.code})` : '');
    setErrors(prev => [...prev, newError]);
  }, []);

  const dismissError = useCallback((errorId: string) => {
    setErrors(prev => prev.map(e => e.id === errorId ? { ...e, dismissed: true } : e));
  }, []);

  const clearErrors = useCallback(() => {
    setErrors([]);
  }, []);

  // Basket state
  const [basket, setBasket] = useState<BasketItem[]>([]);

  // Bootstrap data fetching
  const { loading, bootstrap, refresh, effectiveShop, effectiveProxyBase } =
    useAccountBootstrap({
      shopDomain: shopDomain || null,
      proxyBase: proxyBase || "/apps/paws",
    });

  // Customer data
  const customer = customerJSON;

  // Admin email check - bypasses the start-plan gate; with ?dev=1 also unlocks DevPage
  const isAdminEmail = useMemo(() => {
    const adminEmails = [
      "uzairali19b@gmail.com",
      "sam.a.adams1995@gmail.com",
      // Add more admin emails here
    ];
    const customerEmail = customer?.email?.toLowerCase() || "";
    return adminEmails.some(email => email.toLowerCase() === customerEmail);
  }, [customer?.email]);

  // Dev access check - only visible to admins with URL param
  const isDevAccessible = useMemo(() => {
    const hasDevParam = typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("dev") === "1";
    return isAdminEmail && hasDevParam;
  }, [isAdminEmail]);

  // Dogs from customer metafields (with local state for optimistic updates)
  const { dogs: rawDogs, updateLocalDog } = useDogsFromCustomer(customer);

  // Products (merge from Shopify and bootstrap)
  const products = useMemo<ShopifyProduct[]>(() => {
    const bootstrapProducts =
      bootstrap?.products ||
      bootstrap?.shopifyProducts ||
      bootstrap?.catalog ||
      bootstrap?.allProducts ||
      [];
    const unwrapped = unwrapShopifyProducts(bootstrapProducts);

    // Merge with products passed from Shopify (de-duplicate by ID)
    const merged = [...productsFromShopify];
    const existingIds = new Set(merged.map((p) => String(p.id)));

    unwrapped.forEach((p) => {
      if (!existingIds.has(String(p.id))) {
        merged.push(p);
      }
    });

    return merged;
  }, [bootstrap, productsFromShopify]);

  console.log("AccountProvider: products count =", products);

  // Nutrition engine
  const {
    recipes,
    calculateNutrition,
    compareNutrition,
    suggestBundles,
    recalculateDogNutrition,
  } = useNutritionEngine({ products });

  // Dogs management with nutrition integration
  const {
    dogs,
    dogsById,
    updating,
    getDogById,
    updateDog,
    saveDogWithNutrition,
    softDeleteDog,
    restoreDog,
  } = useDogsManagement({
    customer,
    dogs: rawDogs,
    proxyBase: effectiveProxyBase,
    shop: effectiveShop,
    recalculateDogNutrition,
    onDogsUpdated: refresh,
    updateLocalDog,
  });

  // Subscriptions (normalized)
  const subscriptions = useMemo(
    () => normalizeSubscriptions(bootstrap?.subscriptions),
    [bootstrap?.subscriptions]
  );

  // Active dogs (excluding soft-deleted)
  const activeDogs = useMemo(() => dogs.filter(d => !d.deleted), [dogs]);

  // Delivery config from Qivora (cutoff hours + allowed delivery days)
  const [deliveryCutoffHours, setDeliveryCutoffHours] = useState(36);
  const [deliveryDayNames, setDeliveryDayNames] = useState<string[]>([]);
  useEffect(() => {
    if (!effectiveShop) return;
    fetchAvailableDeliveryDates("", effectiveShop, 4)
      .then((result) => {
        if (result.ok) {
          setDeliveryCutoffHours(result.cutoffHours);
          setDeliveryDayNames(result.deliveryDayNames);
        }
      })
      .catch(() => {});
  }, [effectiveShop]);

  const hasSubs = subscriptions.length > 0;
  const activeSubsCount = subscriptions.filter(isActiveSub).length;
  const nextDeliveryDate = getNextDeliveryDate(bootstrap, deliveryCutoffHours, deliveryDayNames);

  // Upcoming charges
  const upcoming = useMemo<RechargeCharge[]>(() => {
    const data = bootstrap?.upcoming;
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.charges)) return data.charges;
    return [];
  }, [bootstrap?.upcoming]);

  // Orders
  const orders = useMemo<RechargeOrder[]>(() => {
    const data = bootstrap?.orders;
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.orders)) return data.orders;
    return [];
  }, [bootstrap?.orders]);

  // Addresses (with order_attributes containing delivery dates)
  const addresses = useMemo<any[]>(() => {
    const data = bootstrap?.addresses;
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.addresses)) return data.addresses;
    return [];
  }, [bootstrap?.addresses]);

  // Payment methods
  const payments = useMemo<PaymentMethod[]>(() => {
    const data = bootstrap?.paymentMethods;
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.payment_methods)) return data.payment_methods;
    return [];
  }, [bootstrap?.paymentMethods]);

  // Payment update URL
  const startPaymentUpdate = useCallback(async () => {
    const r = await fetchPaymentUpdateUrl(effectiveProxyBase, effectiveShop);
    if (!r.ok) return null;
    return r.body?.url || null;
  }, [effectiveProxyBase, effectiveShop]);

  // Send payment update email (for legacy accounts)
  const handleSendPaymentUpdateEmail = useCallback(async (paymentMethodId?: string) => {
    const r = await sendPaymentUpdateEmail(
      effectiveProxyBase,
      effectiveShop,
      customer?.id ? String(customer.id) : undefined,
      paymentMethodId
    );
    return { ok: r.ok, error: r.error };
  }, [effectiveProxyBase, effectiveShop, customer?.id]);

  // Wrapped refresh that shows global loading overlay
  const refreshWithLoading = useCallback(async (message?: string) => {
    showLoading(message || "Updating your account...");
    try {
      await refresh();
    } finally {
      hideLoading();
    }
  }, [refresh, showLoading, hideLoading]);

  // Skip next delivery
  const skipDelivery = useCallback(async (subscriptionId: string, addressId?: string) => {
    showLoading("Rescheduling your delivery...");
    try {
      const result = await skipNextDelivery(
        effectiveProxyBase,
        effectiveShop,
        subscriptionId,
        customer?.id ? String(customer.id) : undefined,
        addressId
      );
      await refresh(); // Refresh to show updated skip status
      return { newDeliveryDate: result.body?.delivery_date || result.body?.newDeliveryDate };
    } finally {
      hideLoading();
    }
  }, [effectiveProxyBase, effectiveShop, customer?.id, refresh, showLoading, hideLoading]);

  // Basket handlers
  const addToBasket = useCallback((subscriptionId: string, items: Omit<BasketItem, 'subscriptionId'>[]) => {
    setBasket(prev => {
      // Remove existing items for this subscription
      const filtered = prev.filter(item => item.subscriptionId !== subscriptionId);
      // Add new items with subscriptionId
      const newItems = items.map(item => ({ ...item, subscriptionId }));
      return [...filtered, ...newItems];
    });
  }, []);

  const clearBasket = useCallback(() => {
    setBasket([]);
  }, []);

  const removeFromBasket = useCallback((variantId: string) => {
    setBasket(prev => prev.filter(item => item.variantId !== variantId));
  }, []);

  const basketItemCount = useMemo(() => {
    return basket.reduce((sum, item) => sum + Math.abs(item.quantity), 0);
  }, [basket]);

  const checkoutBasket = useCallback(async (newPlanDays?: number | null, oneTimeAddition: boolean = false) => {
    if (basket.length === 0) return;

    showLoading("Updating your subscription...");
    try {
      // Group basket items by subscription
      const bySubscription = new Map<string, BasketItem[]>();
      for (const item of basket) {
        const existing = bySubscription.get(item.subscriptionId) || [];
        existing.push(item);
        bySubscription.set(item.subscriptionId, existing);
      }

      // Update each subscription
      for (const [subscriptionId, items] of bySubscription.entries()) {
        // Build line items from basket changes
        const lineItems = items.map(item => ({
          variantId: item.variantId,
          quantity: item.quantity,
          productTitle: item.productTitle,
          variantTitle: item.variantTitle,
          price: item.price,
        }));

        // Determine new bundle variant ID if plan is changing
        let newBundleVariantId: string | null = null;

        if (newPlanDays && !oneTimeAddition) {
          // Find the subscription to get pack size
          const subscription = subscriptions.find(s => String(s.id) === subscriptionId);
          if (subscription) {
            // Extract pack size from subscription properties
            const packSizeProp = subscription.properties?.find((p: any) => p.name === "_paws_pack_size_grams");
            const packSize = packSizeProp ? parseInt(packSizeProp.value, 10) : 250;

            // Find the bundle product for the new plan
            const planProduct = products.find((p: any) => {
              const title = String(p.title || "").toLowerCase();
              return title.includes(`${newPlanDays} day plan`) || title.includes(`${newPlanDays}-day plan`);
            });

            if (planProduct?.variants) {
              const variant = planProduct.variants.find((v: any) => {
                const sizeFromTitle = parseInt(v.title, 10);
                return sizeFromTitle === packSize;
              });

              if (variant) {
                newBundleVariantId = String(variant.id);
              }
            }
          }
        }

        // Call API to update subscription (with optional plan change)
        const result = await updateSubscriptionMeals(
          effectiveProxyBase,
          effectiveShop,
          subscriptionId,
          lineItems,
          newBundleVariantId,
          customer?.id ? String(customer.id) : undefined
        );

        console.log("Subscription update result:", result);
      }

      // Clear basket
      clearBasket();

      // Refresh data to get updated subscription
      await refresh();
    } catch (error) {
      console.error("Checkout basket error:", error);
      throw error; // Re-throw to show error in UI
    } finally {
      hideLoading();
    }
  }, [basket, subscriptions, products, effectiveProxyBase, effectiveShop, customer, clearBasket, refresh, showLoading, hideLoading]);

  // Context value
  const value: AccountContextValue = {
    // Data
    customer,
    dogs,
    subscriptions,
    upcoming,
    orders,
    addresses,
    payments,
    products,
    recipes,
    // Computed
    activeDogs,
    hasSubs,
    activeSubsCount,
    nextDeliveryDate,

    // Loading
    loading,

    // Error tracking
    errors,
    addError,
    dismissError,
    clearErrors,

    // Global loading overlay
    globalLoading,
    globalLoadingMessage,
    showLoading,
    hideLoading,

    // Actions
    refresh,
    getDogById,
    updateDog,
    saveDogWithNutrition,
    softDeleteDog,
    restoreDog,
    recalculateDogNutrition,
    startPaymentUpdate,
    sendPaymentUpdateEmail: handleSendPaymentUpdateEmail,
    skipDelivery,

    // UI
    nav,
    setNav,
    isDevAccessible,
    isAdminEmail,

    // Basket
    basket,
    basketItemCount,
    addToBasket,
    clearBasket,
    removeFromBasket,
    checkoutBasket,

    // Dog navigation
    selectedDogId,
    setSelectedDogId,

    // Internal
    effectiveShop,
    effectiveProxyBase,
  };

  return (
    <AccountContext.Provider value={value}>
      {children}
    </AccountContext.Provider>
  );
}

/**
 * Hook to access account context
 */
export function useAccount(): AccountContextValue {
  const context = useContext(AccountContext);
  if (!context) {
    throw new Error('useAccount must be used within AccountProvider');
  }
  return context;
}
